import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CalendarCheck } from "lucide-react";

const emptyForm = { name: "", phone: "", email: "", check_in: "", check_out: "", room: "", guests: 1, message: "" };

export const BookingModal = ({ open, onOpenChange, room, rooms = [], hotel }) => {
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) setForm((f) => ({ ...emptyForm, room: room || "" }));
  }, [open, room]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, guests: Number(form.guests) || 1 });
      toast.success("Enquiry sent! We will call you back shortly to confirm your stay.");
      onOpenChange(false);
      setForm(emptyForm);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not send enquiry. Please call us instead."));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "bg-[#FAF8F5] border border-[#0D1B2A]/20 focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/30 rounded-xl px-4 py-3 text-[#0D1B2A] placeholder:text-gray-400 outline-none w-full transition-all text-sm";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#F8F6F0] border-[#C5A059]/30 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#0A192F]">Booking Enquiry</DialogTitle>
          <DialogDescription className="text-[#4A5568] text-sm">
            Send your dates — we call you back to confirm. Prefer to talk now?{" "}
            <a href={hotel?.phone_href || "tel:+13346870000"} className="text-[#C5A059] font-semibold" data-testid="booking-modal-call-link">
              Call {hotel?.phone}
            </a>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="booking-form">
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="booking-name-input" />
            <input required placeholder="Phone *" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="booking-phone-input" />
          </div>
          <input type="email" placeholder="Email (optional)" value={form.email} onChange={set("email")} className={inputCls} data-testid="booking-email-input" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#0D1B2A]/60 block mb-1.5">Check-in</label>
              <input type="date" value={form.check_in} onChange={set("check_in")} className={inputCls} data-testid="booking-checkin-input" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#0D1B2A]/60 block mb-1.5">Check-out</label>
              <input type="date" value={form.check_out} onChange={set("check_out")} className={inputCls} data-testid="booking-checkout-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select value={form.room} onChange={set("room")} className={inputCls} data-testid="booking-room-select">
              <option value="">Any room</option>
              {rooms.map((r) => (
                <option key={r.id || r.name} value={r.name}>{r.name} — ${r.rate}/night</option>
              ))}
            </select>
            <input type="number" min="1" max="10" placeholder="Guests" value={form.guests} onChange={set("guests")} className={inputCls} data-testid="booking-guests-input" />
          </div>
          <textarea placeholder="Anything we should know? (occasion, accessibility, fishing charter…)" value={form.message} onChange={set("message")} rows={3} className={inputCls} data-testid="booking-message-input" />
          <button
            type="submit"
            disabled={sending}
            className="w-full bg-[#0A192F] text-[#F8F6F0] hover:bg-[#C5A059] hover:text-[#0A192F] rounded-full py-4 font-semibold transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="booking-submit-button"
          >
            <CalendarCheck size={17} /> {sending ? "Sending…" : "Send Enquiry"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
